import { API_BRIDGE_EXTENSION_ID } from '../config';

export type BridgeResponseType = 'json' | 'text';

export interface BridgeFetchRequest {
  url: string;
  method?: 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS';
  headers?: Record<string, string>;
  body?: string;
  responseType?: BridgeResponseType;
  cache?: 'default' | 'no-store';
}

export interface BridgeFetchResponse<T = unknown> {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: T;
  url: string;
}

interface BridgeSuccess<T> {
  ok: true;
  data: T;
}

interface BridgeFailure {
  ok: false;
  code: string;
  error: string;
  permission?: string;
}

type BridgeEnvelope<T> = BridgeSuccess<T> | BridgeFailure;

export class ApiBridgeError extends Error {
  constructor(
    message: string,
    public readonly code = 'BRIDGE_ERROR',
    public readonly permission?: string
  ) {
    super(message);
    this.name = 'ApiBridgeError';
  }
}

export async function pingApiBridge(): Promise<{ version: string }> {
  const response = await sendExternal<{ pong: true; version: string }>({ type: 'PING' });
  return { version: response.version };
}

export async function extensionFetch<T = unknown>(
  request: BridgeFetchRequest
): Promise<BridgeFetchResponse<T>> {
  return sendExternal<BridgeFetchResponse<T>>({ type: 'FETCH', request });
}

export async function extensionFetchJson<T>(
  url: string,
  init: Omit<BridgeFetchRequest, 'url' | 'responseType'> = {}
): Promise<BridgeFetchResponse<T>> {
  return extensionFetch<T>({ ...init, url, responseType: 'json' });
}

export async function openBridgeOptions(): Promise<void> {
  await sendExternal<{ opened: true }>({ type: 'OPEN_OPTIONS' });
}

async function sendExternal<T>(message: unknown): Promise<T> {
  if (!globalThis.chrome?.runtime?.sendMessage) {
    throw new ApiBridgeError(
      'GMV Bridge is not available in this browser. Install/enable the companion Chrome or Edge extension.',
      'EXTENSION_UNAVAILABLE'
    );
  }

  return new Promise<T>((resolve, reject) => {
    try {
      chrome.runtime.sendMessage(API_BRIDGE_EXTENSION_ID, message, (response: BridgeEnvelope<T> | undefined) => {
        const lastError = chrome.runtime.lastError;
        if (lastError) {
          reject(new ApiBridgeError(lastError.message || 'Unknown error', 'EXTENSION_UNAVAILABLE'));
          return;
        }
        if (!response) {
          reject(new ApiBridgeError('GMV Bridge returned no response.', 'EMPTY_RESPONSE'));
          return;
        }
        if (response.ok === false) {
          reject(new ApiBridgeError(response.error, response.code, response.permission));
          return;
        }
        resolve(response.data);
      });
    } catch (error) {
      reject(
        error instanceof ApiBridgeError
          ? error
          : new ApiBridgeError(error instanceof Error ? error.message : String(error))
      );
    }
  });
}
